import * as React from "react";
import "./releaseList.css";
import {
  CPLN_HELM_RELEASE_LEGACY_PREFIX,
  CPLN_HELM_RELEASE_PREFIX,
  homeLink,
  linksOf,
  request,
} from "../../../services/cpln";
import { Table } from "../../../newcomponents/table/table";
import { InternalLink } from "../../../newcomponents/table/components/InternalLink";
import { Secret } from "../../../schema/types/secret/entity";
import { Alert } from "antd";
import { getMarketplaceErrorMessage } from "../utils";

interface ReleaseSummary {
  name: string;
  gvc: string;
  template: string;
  isLegacy: boolean;
}

export const MarketplaceReleaseList: React.FC = () => {
  const [isLoadingLegacy, setIsLoadingLegacy] = React.useState(true);
  const [errorLegacy, setErrorLegacy] = React.useState("");
  const [legacyReleases, setLegacyReleases] = React.useState<ReleaseSummary[]>([]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [releases, setReleases] = React.useState<ReleaseSummary[]>([]);

  React.useEffect(() => {
    fetchLegacyReleases();
    fetchReleases();
  }, []);

  async function fetchLegacyReleases() {
    try {
      setErrorLegacy("");
      setIsLoadingLegacy(true);
      let _itemsv1: Secret[] = [];
      let _itemsv2: Secret[] = [];
      let nextLink: string | undefined = undefined;

      const url = homeLink("secret") + "/-query";
      const bodyv1 = {
        kind: "secret",
        spec: {
          match: "all",
          terms: [
            { op: "~", property: "name", value: CPLN_HELM_RELEASE_LEGACY_PREFIX },
            { op: "exists", tag: "console/marketplace" },
          ],
        },
      };
      const resv1 = await request({ method: "post", url: url, body: bodyv1 });
      _itemsv1 = resv1.data.items;
      nextLink = linksOf(resv1.data).next;
      while (nextLink) {
        const { data } = await request({ url: nextLink });
        nextLink = linksOf(data).next;
        _itemsv1 = _itemsv1.concat(data.items);
      }
      _itemsv1 = _itemsv1.filter((i) => {
        let forMarketplace = true;
        if (!i.name.startsWith(CPLN_HELM_RELEASE_LEGACY_PREFIX)) {
          forMarketplace = false;
        }
        return forMarketplace;
      });

      const bodyv2 = {
        kind: "secret",
        spec: {
          match: "all",
          terms: [
            { op: "~", property: "name", value: CPLN_HELM_RELEASE_LEGACY_PREFIX },
            { op: "exists", tag: "cpln/marketplace" },
          ],
        },
      };
      const resv2 = await request({ method: "post", url: url, body: bodyv2 });
      _itemsv2 = resv2.data.items;
      nextLink = linksOf(resv2.data).next;
      while (nextLink) {
        const { data } = await request({ url: nextLink });
        nextLink = linksOf(data).next;
        _itemsv2 = _itemsv2.concat(data.items);
      }
      _itemsv2 = _itemsv2.filter((i) => {
        let forMarketplace = true;
        if (!i.name.startsWith(CPLN_HELM_RELEASE_LEGACY_PREFIX)) {
          forMarketplace = false;
        }
        return forMarketplace;
      });

      setLegacyReleases([
        ..._itemsv1.map((i) => ({
          name: i.name.replace(CPLN_HELM_RELEASE_LEGACY_PREFIX, ""),
          gvc: (i.tags || {})["console/marketplace-gvc"] || "",
          template: (i.tags || {})["console/marketplace-template"] || "",
          isLegacy: true,
        })),
        ..._itemsv2.map((i) => ({
          name: i.name.replace(CPLN_HELM_RELEASE_LEGACY_PREFIX, ""),
          gvc: (i.tags || {})["cpln/marketplace-gvc"] || "",
          template: (i.tags || {})["cpln/marketplace-template"] || "",
          isLegacy: true,
        })),
      ]);
      setIsLoadingLegacy(false);
    } catch (e) {
      setIsLoadingLegacy(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      setErrorLegacy(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
    }
  }

  async function fetchReleases() {
    try {
      setError("");
      setIsLoading(true);
      let _items: Secret[] = [];
      let nextLink: string | undefined = undefined;

      const url = homeLink("secret") + "/-query";
      const body = {
        kind: "secret",
        spec: {
          match: "all",
          terms: [
            { op: "~", property: "name", value: CPLN_HELM_RELEASE_PREFIX },
            { op: "exists", tag: "cpln/marketplace" },
          ],
        },
      };
      const { data } = await request({ method: "post", url: url, body: body });
      _items = data.items;
      nextLink = linksOf(data).next;

      while (nextLink) {
        const { data } = await request({ url: nextLink, body: body });
        nextLink = linksOf(data).next;
        _items = _items.concat(data.items);
      }
      _items = _items.filter((i) => {
        let forMarketplace = true;
        if (!i.name.startsWith(CPLN_HELM_RELEASE_PREFIX)) {
          forMarketplace = false;
        }
        return forMarketplace;
      });
      let _addedItemNames: string[] = [];
      let _deduplicatedItems: Secret[] = [];
      for (const _item of _items) {
        const name = (_item.tags || {})["name"] || "";
        if (!name) {
          continue;
        }
        if (_addedItemNames.includes(name)) {
          continue;
        }
        _addedItemNames.push(name);
        _deduplicatedItems.push(_item);
      }

      setReleases(
        _deduplicatedItems.map((i) => ({
          name: (i.tags || {})["name"],
          gvc: (i.tags || {})["cpln/marketplace-gvc"] || "",
          template: (i.tags || {})["cpln/marketplace-template"] || "",
          isLegacy: false,
        })),
      );
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
    }
  }

  if (error || errorLegacy) {
    return <Alert type={"error"} message="Error" description={error || errorLegacy} />;
  }

  return (
    <>
      <Table<ReleaseSummary>
        tableId="marketplace-apps"
        data={[...legacyReleases, ...releases]}
        enableFilter
        title={"Template Releases"}
        columns={[
          {
            id: "name",
            label: "Name",
            cell: (p) => {
              const item = p.row.original;
              return (
                <div className="flex items-center" style={{ height: 56 }}>
                  <InternalLink label={item.name} to={item.isLegacy ? `legacy/${item.name}` : item.name} />
                </div>
              );
            },
          },
          { id: "gvc", label: "GVC" },
          { id: "template", label: "Template" },
        ]}
        isLoading={isLoading || isLoadingLegacy}
      />
    </>
  );
};
